import {useGetPartnerInfoQuery} from "api/requests/partner-level";
import {PartnerType} from "declarations";
import React, {PropsWithChildren, useContext, useMemo, createContext} from "react";
import {isInternalUser} from "utils/userUtils";
import {useAuthContext} from "./AuthContext";

export interface PartnerContextProps {
  partnerType: PartnerType | null;
  isFpPartner: boolean;
  isPreChargeOff: boolean;
  partnerCode: string;
  partnerName: string;
  isLoading: boolean;
  hideCommsFromTimeline: boolean;
}

const PartnerContext = createContext<PartnerContextProps | undefined>(undefined);

export const PartnerProvider = ({children}: PropsWithChildren) => {
  const {isLoggedIn, partnerCode, username, session} = useAuthContext();
  const hasSession = !!session?.tokens?.accessToken;
  const isEnabled = hasSession && (isInternalUser(username) ? !!partnerCode : !!isLoggedIn);
  const {data, isLoading} = useGetPartnerInfoQuery({partnerCode}, {enabled: !!isEnabled});

  const partnerDetails = useMemo<PartnerContextProps>(
    () => ({
      partnerType: data?.partner_type ?? null,
      isFpPartner: data?.first_party_enabled ?? false,
      isPreChargeOff: data?.is_pre_charge_off ?? false,
      partnerCode: data?.partner_code ?? "",
      partnerName: data?.partner_name ?? "",
      isLoading,
      hideCommsFromTimeline: data?.hide_comms_from_timeline ?? false,
    }),
    [data, isLoading]
  );

  return <PartnerContext.Provider value={partnerDetails}>{children}</PartnerContext.Provider>;
};

export const usePartnerContext = (): PartnerContextProps => {
  const context = useContext(PartnerContext);

  if (!context) {
    throw new Error("usePartnerContext must be used inside PartnerProvider");
  }

  return context;
};
